import React, { useEffect, useState, useRef, useMemo } from 'react';
import { getCurrentBrowserFingerPrint } from '@rajesh896/broprint.js';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import axios from 'axios';
import TVTestPlayer from '../components/TVTestPlayer';
import io from 'socket.io-client';
import './QRCode.css';
import trillBoardLogo from '../Trillboard Logo.png';
// import videoImage from '../Group 1686555205.png'; // Removed unused import
import { QRCode } from 'react-qrcode-logo';
import useNoSleep from '../hooks/useNoSleep';
import resolveRuntimeAssetUrl from '../utils/runtimeAssetUrl';

// Import comprehensive device detection
import deviceManager from '../utils/DeviceManager';
import logger from '../utils/Logger.js';
import { identifyDevice, screenEvents } from '../lib/posthog';
import { collectNetworkSignals } from '../utils/NetworkSignalCollector';
import {
    chooseBootstrapFingerprint,
    getAttachedScreenId,
    getReusableCachedScreenId,
    shouldClearNativeCachedIdentity,
    shouldUseStoredDeviceToken
} from '../utils/bootstrapIdentity';
import {
    CTV_BOOTSTRAP_VISIBLE_FALLBACK_MS,
    hasCachedNativeIdentity,
    shouldShowBootstrapLoader
} from '../utils/ctvBootstrapVisibility';
// WebSensingBridge's own scheduleInit() handles late camera/mic permission prompts post-pairing.
// We no longer chain those prompts off the geolocation success callback (geolocation removed; partner WebViews crash on the prompt).

function readLocalStorageItem(key, fallback = '') {
    try {
        return localStorage.getItem(key) || fallback;
    } catch (_) {
        return fallback;
    }
}

// Persist screenId to localStorage + window global so PostHog error telemetry
// can include it (getDeviceContext reads localStorage, overlay reads window).
function persistScreenId(screenId, fingerprint = null) {
    try {
        if (screenId) {
            localStorage.setItem('screenId', screenId);
            if (fingerprint) {
                localStorage.setItem('trillboard_screen_identity_fingerprint', fingerprint);
            }
            window.__TRILLBOARD_SCREEN_ID__ = screenId;
        } else {
            localStorage.removeItem('screenId');
            localStorage.removeItem('trillboard_screen_identity_fingerprint');
            window.__TRILLBOARD_SCREEN_ID__ = null;
        }
    } catch (e) {
        // Ignore storage errors on restricted browsers
    }
}

function normalizeBaseUrl(value) {
    if (!value) return '';
    return String(value).trim().replace(/\/+$/, '');
}

const FINGERPRINT_STAGE_TIMEOUT_MS = 4000;
const FINGERPRINT_BOOTSTRAP_TIMEOUT_MS = 7000;
const CHECK_SCREEN_REQUEST_TIMEOUT_MS = 10000;
const CHECK_SCREEN_RETRY_BASE_DELAY_MS = 1500;
const CHECK_SCREEN_RETRY_MAX_DELAY_MS = 5000;
const CHECK_SCREEN_MAX_RETRIES_WITHOUT_CACHE = 2;
const CONTENT_PREFETCH_TIMEOUT_MS = 12000;
const ATTACH_SCREEN_TIMEOUT_MS = 15000;
const BACKGROUND_RECONNECT_TIMEOUT_MS = 10000;

const enterCachedScreenState = ({
    screenId,
    fingerprint,
    setMyId,
    setConnected,
    setLoader,
    setCachedId,
    reason
}) => {
    if (!screenId) return;

    logger.warn("Entering cached screen state immediately", { screenId, reason }, 'QRCode');
    persistScreenId(screenId, fingerprint);
    setMyId(screenId);
    setCachedId(screenId);
    setConnected(true);
    setLoader(false);
};

const clearCachedScreenState = ({
    setMyId,
    setConnected,
    setLoader,
    setCachedId,
    setPrefetched,
    reason
}) => {
    logger.warn("Clearing cached screen state", { reason }, 'QRCode');
    persistScreenId('');
    setCachedId('');
    setMyId('');
    setConnected(false);
    setPrefetched(null);
    setLoader(false);
};

function withTimeout(promise, timeoutMs, label) {
    return new Promise((resolve, reject) => {
        let settled = false;
        const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new Error(`${label}_timeout_${timeoutMs}ms`));
        }, timeoutMs);

        Promise.resolve(promise)
            .then((value) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(value);
            })
            .catch((error) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                reject(error);
            });
    });
}

function buildStableCustomFingerprint() {
    const screenPart = `${window.screen.width}x${window.screen.height}`;
    const pixelRatioPart = window.devicePixelRatio.toString();
    const userAgentHash = navigator.userAgent.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
    }, 0);
    const languagePart = navigator.language || 'unknown';
    const timezonePart = new Date().getTimezoneOffset();
    const hardwarePart = navigator.hardwareConcurrency || 0;
    const platformPart = navigator.platform || 'unknown';
    const touchPart = navigator.maxTouchPoints || 0;

    return `STABLE-${screenPart}-${pixelRatioPart}-${Math.abs(userAgentHash)}-${languagePart}-${timezonePart}-${hardwarePart}-${platformPart}-${touchPart}`;
}

const QRcode = () => {
    const [myId, setmyId] = useState('');
    const [connected, setconnected] = useState(false);
    const [myFingerPrint, setmyFingerPrint] = useState('');
    const [deviceToken, setDeviceToken] = useState('');
    const [cachedScreenId, setCachedScreenId] = useState(() => {
        return readLocalStorageItem('screenId');
    });
    const [loader, setloader] = useState(false);
    const [mysocket, setmysocket] = useState(null);
    const debugMode = false;
    const [storageBlocked, setStorageBlocked] = useState(false); // Storage availability state
    const [pairingCode, setPairingCode] = useState('');
    const [pairingCodeExpiresAt, setPairingCodeExpiresAt] = useState(null);
    const [prefetchedContent, setPrefetchedContent] = useState(null); // Prefetched content for instant display
    const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const urlFingerprint = useMemo(
        () => urlParams.get('fingerprint') || urlParams.get('fp') || '',
        [urlParams]
    );
    const hasUrlFingerprint = Boolean(urlFingerprint);
    const urlDeviceToken = useMemo(
        () => urlParams.get('device_token') || '',
        [urlParams]
    );
    const urlScreenId = useMemo(
        () => urlParams.get('screenId') || urlParams.get('screen_id') || '',
        [urlParams]
    );
    const nativeFirstInstallTimeMs = useMemo(
        () => urlParams.get('native_first_install_time_ms') || urlParams.get('nativeFirstInstallTimeMs') || '',
        [urlParams]
    );
    const strictInstall = useMemo(
        () => ['1', 'true'].includes(String(urlParams.get('strict_install') || '').trim().toLowerCase()),
        [urlParams]
    );
    const isNativeCtvBootstrap = useMemo(() => {
        const platform = String(urlParams.get('platform') || '').trim().toLowerCase();
        return hasUrlFingerprint && (
            urlParams.get('ctv') === '1' ||
            ['firetv', 'androidtv', 'tizen', 'webos', 'vidaa'].includes(platform)
        );
    }, [hasUrlFingerprint, urlParams]);
    const appBase = useMemo(() => {
        return (
            normalizeBaseUrl(urlParams.get('appBase')) ||
            normalizeBaseUrl(urlParams.get('apiBase')) ||
            normalizeBaseUrl(window.__TRILLBOARDS_APP_BASE) ||
            normalizeBaseUrl(window.__TRILLBOARDS_API_BASE) ||
            normalizeBaseUrl(process.env.REACT_APP_APP_URL) ||
            normalizeBaseUrl(process.env.REACT_APP_API_URL) ||
            'https://api.trillboards.com'
        );
    }, [urlParams]);
    const buildAppUrl = useMemo(() => {
        return (path) => `${appBase}${path.startsWith('/') ? path : `/${path}`}`;
    }, [appBase]);
    const buildCheckScreenUrl = useMemo(() => {
        return (fingerprint) => {
            const query = new URLSearchParams();
            if (isNativeCtvBootstrap) {
                query.set('strict_install', '1');
                if (nativeFirstInstallTimeMs) {
                    query.set('native_first_install_time_ms', nativeFirstInstallTimeMs);
                }
            }
            if (urlScreenId) {
                query.set('screen_id', urlScreenId);
            }
            const suffix = query.toString() ? `?${query.toString()}` : '';
            return buildAppUrl(
                `/v2/earner/check-screen/${encodeURIComponent(fingerprint)}${suffix}`
            );
        };
    }, [buildAppUrl, isNativeCtvBootstrap, nativeFirstInstallTimeMs, urlScreenId]);

    const isDetachOrDeleteSignal = (payload = {}) => {
        const action = payload?.action;
        const reason = payload?.reason;
        return action === 'screen_deleted' ||
            action === 'device_detached_from_inventory_screen' ||
            action === 'screen_detached' ||
            action === 'screen_unpaired' ||
            action === 'detached' ||
            action === 'deleted' ||
            reason === 'device_detached_from_inventory_screen';
    };

    const resetToPairingMode = (payload = {}, socketInstance = null) => {
        logger.warn("Screen binding cleared - returning to pairing mode", {
            action: payload?.action,
            reason: payload?.reason,
            screen_id: payload?.screen_id || payload?.screenId,
        }, "QRCode");
        setconnected(false);
        persistScreenId('');
        setmyId('');
        setCachedScreenId('');
        setPrefetchedContent(null);
        setloader(false);
        if (socketInstance?.connected) {
            socketInstance.disconnect();
        }
    };

    // Immediate device detection (available synchronously)
    const deviceInfo = deviceManager.deviceInfo;
    const viewport = deviceManager.viewport;
    const capabilities = deviceManager.capabilities;
    
    logger.trace('Device detection data available', {
        deviceInfo,
        viewport,
        capabilities
    }, 'QRCode');
    
    // Enhanced screensaver prevention for QR code setup phase
    useNoSleep();
    
    // Polling state for checking screen connection after QR scan
    const [isPolling, setIsPolling] = useState(false);
    const pollingIntervalRef = useRef(null);
    const reconnectIntervalRef = useRef(null);
    const fingerprintBootstrapResolvedRef = useRef(false);

    // Detect Safari browser
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    logger.trace('Browser detection', { browser: 'Safari', isSafari }, 'QRCode');

    // Test if ANY storage method is available
    const testStorageAvailability = () => {
        const testKey = '_storage_test_';
        const testValue = 'test';

        // Test localStorage
        try {
            localStorage.setItem(testKey, testValue);
            localStorage.removeItem(testKey);
            return { available: true, method: 'localStorage' };
        } catch (e) {}

        // Test sessionStorage
        try {
            sessionStorage.setItem(testKey, testValue);
            sessionStorage.removeItem(testKey);
            return { available: true, method: 'sessionStorage' };
        } catch (e) {}

        // Test cookies
        try {
            document.cookie = `${testKey}=${testValue}; path=/`;
            if (document.cookie.includes(testKey)) {
                document.cookie = `${testKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
                return { available: true, method: 'cookie' };
            }
        } catch (e) {}

        return { available: false, method: null };
    };

    // Device install ID cookie helpers (backup for localStorage)
    const getCookieDeviceId = () => {
        try {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; trillboard_unique_device_id=`);
            if (parts.length === 2) {
                const deviceId = parts.pop().split(';').shift();
                return deviceId || null;
            }
            return null;
        } catch (error) {
            logger.error("Failed to read device ID cookie", { error: error.message }, 'QRCode');
            return null;
        }
    };

    const setCookieDeviceId = (deviceId) => {
        try {
            const maxAge = 31536000; // 1 year in seconds
            document.cookie = `trillboard_unique_device_id=${deviceId}; max-age=${maxAge}; path=/; SameSite=Strict`;
        } catch (error) {
            logger.error("Failed to set device ID cookie", { error: error.message }, 'QRCode');
        }
    };

    // Generate unique device ID for fingerprint enhancement
    const getOrCreateUniqueDeviceId = () => {
        let deviceId = null;

        try {
            deviceId = localStorage.getItem('trillboard_unique_device_id');
        } catch (_) {
            deviceId = null;
        }

        if (!deviceId) {
            deviceId = getCookieDeviceId();
        }

        if (!deviceId) {
            const installTime = Date.now().toString(36);
            const randomComponent = Math.random().toString(36).substring(2, 8);
            deviceId = `DEV-${installTime}-${randomComponent}`;
            logger.info("Generated new unique device ID", { deviceId }, 'QRCode');
        }

        try {
            localStorage.setItem('trillboard_unique_device_id', deviceId);
        } catch (_) {}

        setCookieDeviceId(deviceId);
        return deviceId;
    };

    // Enhance fingerprint with unique device ID
    const enhanceFingerprint = (baseFingerprint) => {
        const deviceId = getOrCreateUniqueDeviceId();
        return `${baseFingerprint}_${deviceId}`;
    };

    // Generate session-based device identification for duplicate prevention
    const getOrCreateSessionId = () => {
        let sessionId = sessionStorage.getItem('trillboard_device_session');
        if (!sessionId) {
            // Create session ID based on stable hardware characteristics
            const hardwareElements = [
                window.screen.width,
                window.screen.height,
                window.devicePixelRatio,
                navigator.hardwareConcurrency || 0,
                navigator.maxTouchPoints || 0,
                navigator.userAgent.slice(-50)
            ];

            const hardwareHash = hardwareElements.join('|');
            const timestamp = Date.now();
            sessionId = `SESS-${hardwareHash.split('').reduce((a, b) => {
                a = ((a << 5) - a) + b.charCodeAt(0);
                return a & a;
            }, 0)}-${timestamp}`;

            sessionStorage.setItem('trillboard_device_session', sessionId);
            logger.debug('Session ID created', { sessionId }, 'QRCode');
        } else {
            logger.trace('Using existing session ID', { sessionId }, 'QRCode');
        }
        return sessionId;
    };

    // Generate session fingerprint combining session + hardware characteristics
    const generateSessionFingerprint = (sessionId) => {
        const sessionElements = [
            sessionId,
            window.screen.width,
            window.screen.height,
            window.devicePixelRatio,
            navigator.userAgent.slice(-30),
            new Date().getTimezoneOffset()
        ];

        return `SF-${sessionElements.join('|').split('').reduce((a, b) => {
            a = ((a << 5) - a) + b.charCodeAt(0);
            return a & a;
        }, 0)}`;
    };

    // ========================================
    // COOKIE UTILITY FUNCTIONS
    // Added for backwards-compatible fingerprint persistence
    // ========================================

    // Get fingerprint from cookie (survives privacy mode better than localStorage)
    const getCookieFingerprint = () => {
        try {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; trillboard_fingerprint=`);
            if (parts.length === 2) {
                const fingerprint = parts.pop().split(';').shift();
                return fingerprint || null;
            }
            return null;
        } catch (error) {
            logger.error("Failed to read fingerprint cookie", { error: error.message }, 'QRCode');
            return null;
        }
    };

    // Set fingerprint cookie with 1-year expiration
    const setCookieFingerprint = (fingerprint) => {
        try {
            const maxAge = 31536000; // 1 year in seconds
            document.cookie = `trillboard_fingerprint=${fingerprint}; max-age=${maxAge}; path=/; SameSite=Strict`;
            logger.debug("Fingerprint cookie set", { fingerprint }, 'QRCode');
        } catch (error) {
            logger.error("Failed to set fingerprint cookie", { error: error.message }, 'QRCode');
        }
    };

    const getCookieDeviceToken = () => {
        try {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; trillboard_device_token=`);
            if (parts.length === 2) {
                const token = parts.pop().split(';').shift();
                return token || null;
            }
            return null;
        } catch (error) {
            logger.error("Failed to read device token cookie", { error: error.message }, 'QRCode');
            return null;
        }
    };

    const setCookieDeviceToken = (token) => {
        try {
            const maxAge = 31536000; // 1 year in seconds
            document.cookie = `trillboard_device_token=${token}; max-age=${maxAge}; path=/; SameSite=Strict`;
        } catch (error) {
            logger.error("Failed to set device token cookie", { error: error.message }, 'QRCode');
        }
    };

    const getCookieDeviceTokenFingerprint = () => {
        try {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; trillboard_device_token_fingerprint=`);
            if (parts.length === 2) {
                const tokenFingerprint = parts.pop().split(';').shift();
                return tokenFingerprint || null;
            }
            return null;
        } catch (error) {
            logger.error("Failed to read device token fingerprint cookie", { error: error.message }, 'QRCode');
            return null;
        }
    };

    const setCookieDeviceTokenFingerprint = (fingerprint) => {
        try {
            const maxAge = 31536000; // 1 year in seconds
            document.cookie = `trillboard_device_token_fingerprint=${fingerprint}; max-age=${maxAge}; path=/; SameSite=Strict`;
        } catch (error) {
            logger.error("Failed to set device token fingerprint cookie", { error: error.message }, 'QRCode');
        }
    };

    const clearCookie = (name) => {
        try {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        } catch (error) {
            logger.error("Failed to clear cookie", { name, error: error.message }, 'QRCode');
        }
    };

    const getStoredDeviceToken = () => {
        try {
            const stored = localStorage.getItem('trillboard_device_token');
            if (stored) return stored;
        } catch (_) {}
        return getCookieDeviceToken();
    };

    const getStoredDeviceTokenFingerprint = () => {
        try {
            const stored = localStorage.getItem('trillboard_device_token_fingerprint');
            if (stored) return stored;
        } catch (_) {}
        return getCookieDeviceTokenFingerprint();
    };

    const clearStoredDeviceToken = () => {
        try {
            localStorage.removeItem('trillboard_device_token');
            localStorage.removeItem('trillboard_device_token_fingerprint');
        } catch (_) {}
        clearCookie('trillboard_device_token');
        clearCookie('trillboard_device_token_fingerprint');
        setDeviceToken('');
    };

    const persistDeviceToken = (token, fingerprint = null) => {
        if (!token) return;
        try {
            localStorage.setItem('trillboard_device_token', token);
            if (fingerprint) {
                localStorage.setItem('trillboard_device_token_fingerprint', fingerprint);
            }
        } catch (_) {}
        setCookieDeviceToken(token);
        if (fingerprint) {
            setCookieDeviceTokenFingerprint(fingerprint);
        }
        setDeviceToken(token);
    };

    const handleDeviceTokenResponse = (response, fingerprint = null) => {
        const token = response?.data?.data?.device_token;
        if (token) {
            persistDeviceToken(token, fingerprint || myFingerPrint || null);
        }
    };

    const getDeviceHeaders = (contentType = 'application/json') => ({
        ...(contentType ? { "Content-Type": contentType } : {}),
        ...(deviceToken ? { 'X-Device-Token': deviceToken } : {})
    });

    useEffect(() => {
        const staleCachedScreenId = readLocalStorageItem('screenId');
        if (!shouldClearNativeCachedIdentity({
            isNativeCtvBootstrap,
            strictInstall,
            urlDeviceToken,
            cachedScreenId: staleCachedScreenId
        })) {
            return;
        }

        logger.warn("Native strict bootstrap has no device token - clearing cached web identity", {
            cachedScreenId: staleCachedScreenId
        }, 'QRCode');
        clearStoredDeviceToken();
        persistScreenId('');
        setCachedScreenId('');
        setmyId('');
        setconnected(false);
        setPrefetchedContent(null);
    }, [isNativeCtvBootstrap, strictInstall, urlDeviceToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // The helper closures below intentionally stay out of the deps array so
    // token restoration only responds to fingerprint/bootstrap changes.
    useEffect(() => {
        if (!myFingerPrint) return;

        // URL device token from native CTV agent (SharedPreferences) is authoritative —
        // it survives fingerprint changes across app updates, so skip the fingerprint
        // match check. Persist it so subsequent check-screen calls include it.
        if (urlDeviceToken) {
            logger.info("Using device token from URL parameter (native agent)", null, 'QRCode');
            persistDeviceToken(urlDeviceToken, myFingerPrint);
            setDeviceToken(urlDeviceToken);
            return;
        }

        const storedToken = getStoredDeviceToken();
        if (!storedToken) {
            return;
        }

        const storedTokenFingerprint = getStoredDeviceTokenFingerprint();
        if (!shouldUseStoredDeviceToken({
            currentFingerprint: myFingerPrint,
            storedTokenFingerprint,
            hasUrlFingerprint
        })) {
            logger.warn("Ignoring stored device token because it is not bound to the active fingerprint", {
                currentFingerprint: myFingerPrint,
                storedTokenFingerprint
            }, 'QRCode');
            clearStoredDeviceToken();
            return;
        }

        setDeviceToken(storedToken);
    }, [hasUrlFingerprint, myFingerPrint, urlDeviceToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // ========================================
    // DETERMINISTIC FALLBACK FINGERPRINT
    // Uses FingerprintJS Open Source (no Math.random)
    // Replaces non-deterministic fallback for stability
    // ========================================

    const generateDeterministicFallback = async () => {
        logger.info("Generating stable deterministic fallback fingerprint", null, 'QRCode');
        return buildStableCustomFingerprint();
    };

    // Bootstrap fingerprinting must run once on mount; rerunning on helper
    // identity churn would rewrite persisted identity during setup.
    useEffect(() => {
        let cancelled = false;

        const commitFingerprint = (fingerprint, source, details = {}) => {
            if (!fingerprint || cancelled || fingerprintBootstrapResolvedRef.current) {
                return false;
            }

            fingerprintBootstrapResolvedRef.current = true;
            logger.info("Fingerprint bootstrap resolved", { source, ...details }, 'QRCode');
            setmyFingerPrint(fingerprint);

            try {
                localStorage.setItem('trillboard_fingerprint', fingerprint);
            } catch (_) {}
            setCookieFingerprint(fingerprint);
            return true;
        };

        const failFingerprintBootstrap = (error) => {
            if (cancelled || fingerprintBootstrapResolvedRef.current) {
                return;
            }

            const storageTest = testStorageAvailability();
            logger.error("Fingerprint bootstrap failed with no usable fallback", {
                error: error?.message,
                storageAvailable: storageTest.available,
                storageMethod: storageTest.method || null
            }, 'QRCode');

            fingerprintBootstrapResolvedRef.current = true;
            if (!storageTest.available) {
                setStorageBlocked(true);
            }
            setloader(false);
        };

        const resolveFromStableFallback = async (reason, extra = {}) => {
            try {
                const deterministicFingerprint = await generateDeterministicFallback();
                const enhancedFingerprint = enhanceFingerprint(deterministicFingerprint);
                if (commitFingerprint(enhancedFingerprint, reason, extra)) {
                    return true;
                }
            } catch (error) {
                logger.error("Stable fallback fingerprint generation failed", {
                    error: error?.message,
                    reason
                }, 'QRCode');
            }
            return false;
        };

        // Check if fingerprint is provided in URL (Android/Fire TV/Tablet WebView case)
        // These CTV agents pass their native fingerprint via URL parameter
        let storedFingerprint = null;
        try {
            storedFingerprint = getCookieFingerprint() || localStorage.getItem('trillboard_fingerprint');
        } catch (err) {
            storedFingerprint = getCookieFingerprint();
        }

        const fingerprintChoice = chooseBootstrapFingerprint({
            urlFingerprint,
            storedFingerprint
        });

        if (fingerprintChoice.shouldResetStoredIdentity) {
            logger.warn("URL fingerprint differs from stored fingerprint - clearing stale screen/token identity", {
                urlFingerprint,
                storedFingerprint
            }, 'QRCode');
            clearStoredDeviceToken();
            persistScreenId('');
        }

        if (fingerprintChoice.fingerprint) {
            logger.info(
                fingerprintChoice.source === 'url'
                    ? "Using fingerprint from URL parameter (authoritative bootstrap)"
                    : "Using stored fingerprint",
                { fingerprint: fingerprintChoice.fingerprint },
                'QRCode'
            );
            commitFingerprint(fingerprintChoice.fingerprint, `bootstrap_${fingerprintChoice.source}`);
            return;
        }

        const fingerprintWatchdog = setTimeout(() => {
            if (cancelled || fingerprintBootstrapResolvedRef.current) {
                return;
            }

            logger.warn("Fingerprint bootstrap timed out - forcing stable fallback", {
                timeoutMs: FINGERPRINT_BOOTSTRAP_TIMEOUT_MS
            }, 'QRCode');

            resolveFromStableFallback('bootstrap_watchdog', {
                timeoutMs: FINGERPRINT_BOOTSTRAP_TIMEOUT_MS
            }).then((resolved) => {
                if (!resolved) {
                    failFingerprintBootstrap(new Error('fingerprint_bootstrap_watchdog_failed'));
                }
            });
        }, FINGERPRINT_BOOTSTRAP_TIMEOUT_MS);

        // Browser flow - generate fingerprint as usual
        // Try the primary fingerprinting method first
        const fpPromise = withTimeout(
            Promise.resolve(FingerprintJS).then((FingerprintJSApi) => FingerprintJSApi.load()),
            FINGERPRINT_STAGE_TIMEOUT_MS,
            'fingerprintjs_load'
        ).catch(() => {
                console.warn('[FPJS] loader failed, continuing without fingerprint');
                return null;
            });

        fpPromise
            .then(fp => {
                if (!fp) throw new Error('FPJS loader unavailable');
                return withTimeout(
                    fp.get(),
                    FINGERPRINT_STAGE_TIMEOUT_MS,
                    'fingerprintjs_get'
                );
            })
            .then(result => {
                logger.info("Primary fingerprint generated", { visitorId: result.visitorId }, 'QRCode');
                if (result?.visitorId) {
                    const enhancedFingerprint = enhanceFingerprint(result.visitorId);
                    commitFingerprint(enhancedFingerprint, 'primary_fingerprintjs');
                } else {
                    throw new Error("No visitorId returned");
                }
            })
            .catch(err => {
                logger.warn("Primary fingerprint failed, trying secondary method", { error: err.message }, 'QRCode');
                // Try secondary fingerprinting method
                withTimeout(
                    getCurrentBrowserFingerPrint(),
                    FINGERPRINT_STAGE_TIMEOUT_MS,
                    'broprint_get'
                )
                    .then(fingerprint => {
                        logger.info("Secondary fingerprint generated", { fingerprint }, 'QRCode');
                        if (fingerprint) {
                            const enhancedFingerprint = enhanceFingerprint(fingerprint);
                            commitFingerprint(enhancedFingerprint, 'secondary_broprint');
                        } else {
                            throw new Error("Secondary fingerprinting failed");
                        }
                    })
                    .catch(async (secondaryErr) => {
                        logger.warn("Secondary fingerprint failed, checking stored fingerprints", { error: secondaryErr.message }, 'QRCode');

                        try {
                            // ========================================
                            // BACKWARDS-COMPATIBLE PERSISTENCE STRATEGY
                            // Check cookie first (survives privacy mode)
                            // Check localStorage second (existing devices)
                            // Generate deterministic fallback third (no Math.random)
                            // ========================================

                            // 1. Try cookie first (best for privacy mode)
                            let storedFingerprint = getCookieFingerprint();
                            if (storedFingerprint) {
                                logger.info("Using stored fingerprint from cookie", { fingerprint: storedFingerprint, source: 'cookie' }, 'QRCode');
                                commitFingerprint(storedFingerprint, 'stored_cookie_fallback');
                                return;
                            }

                            // 2. Try localStorage second (backwards compatibility for existing devices)
                            storedFingerprint = localStorage.getItem('trillboard_fingerprint');
                            if (storedFingerprint) {
                                logger.info("Using stored fingerprint from localStorage", { fingerprint: storedFingerprint, source: 'localStorage' }, 'QRCode');
                                commitFingerprint(storedFingerprint, 'stored_localstorage_fallback');
                                return;
                            }

                            // 3. Generate new deterministic fingerprint (no Math.random!)
                            logger.info("No stored fingerprint found, generating new deterministic fingerprint", null, 'QRCode');
                            const deterministicFingerprint = await generateDeterministicFallback();
                            const enhancedFingerprint = enhanceFingerprint(deterministicFingerprint);
                            logger.info("New deterministic fingerprint created", { fingerprint: enhancedFingerprint, method: 'FingerprintJS-OSS' }, 'QRCode');
                            commitFingerprint(enhancedFingerprint, 'deterministic_fallback');

                        } catch (storageError) {
                            // Storage is completely blocked - test availability and show blocking error
                            logger.error("Storage access failed - testing availability", { error: storageError.message }, 'QRCode');

                            const storageTest = testStorageAvailability();
                            if (!storageTest.available) {
                                logger.error("All storage methods blocked - cannot persist fingerprint", null, 'QRCode');
                                fingerprintBootstrapResolvedRef.current = true;
                                setStorageBlocked(true);
                                setloader(false);
                                // Do NOT set fingerprint - honest failure
                                return;
                            }

                            // If storage is available but something else failed, this is unexpected
                            logger.error("Unexpected error in fallback fingerprint logic", { error: storageError.message }, 'QRCode');
                            failFingerprintBootstrap(storageError);
                        }
                    });
            });

        return () => {
            cancelled = true;
            clearTimeout(fingerprintWatchdog);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        if (myFingerPrint) {
            logger.info("Fingerprint set successfully", { fingerprint: myFingerPrint }, 'QRCode');
        }
    }, [myFingerPrint]);

    useEffect(() => {
        if (myId) {
            setCachedScreenId(myId);
        }
    }, [myId]);

    // PostHog: Identify device when connected. Device characteristics here are
    // static for the page lifetime, so only the pairing state should retrigger.
    useEffect(() => {
        if (myFingerPrint && connected && myId) {
            identifyDevice(myFingerPrint, { screenId: myId });
            screenEvents.connected(myFingerPrint, myId, {
                deviceInfo: {
                    isTV: deviceInfo.isTV,
                    screenSize: `${deviceInfo.screenWidth}x${deviceInfo.screenHeight}`,
                },
            });
        }
    }, [myFingerPrint, connected, myId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Set CSS custom properties and log device capabilities
    // These CSS vars capture the initial device snapshot used for the session.
    useEffect(() => {
        // Set CSS custom properties for viewport optimization
        const root = document.documentElement;
        
        root.style.setProperty('--dm-viewport-width', viewport.cssWidth);
        root.style.setProperty('--dm-viewport-height', viewport.cssHeight);
        root.style.setProperty('--dm-viewport-top', viewport.cssTop);
        root.style.setProperty('--dm-viewport-left', viewport.cssLeft);
        
        logger.info('Device detection complete and CSS properties set', {
            isTV: deviceInfo.isTV,
            isFireTV: deviceInfo.isFireTV,
            isSamsungTV: deviceInfo.isSamsungTV,
            isLGTV: deviceInfo.isLGTV,
            screenSize: `${deviceInfo.screenWidth}x${deviceInfo.screenHeight}`,
            aspectRatio: deviceInfo.aspectRatio.toFixed(2),
            pixelRatio: deviceInfo.pixelRatio,
            browser: {
                isChrome: deviceInfo.isChrome,
                isSafari: deviceInfo.isSafari,
                isFirefox: deviceInfo.isFirefox
            },
            viewport: {
                width: viewport.width,
                height: viewport.height,
                usableWidth: viewport.usableWidth,
                usableHeight: viewport.usableHeight,
                cssWidth: viewport.cssWidth,
                cssHeight: viewport.cssHeight
            },
            capabilities: {
                canPlayVideo: capabilities.canPlayVideo,
                canCreateIframe: capabilities.canCreateIframe,
                supportsAutoplay: capabilities.supportsAutoplay,
                isHighPerformance: capabilities.isHighPerformance
            },
            reactPlayer: {
                shouldUse: deviceManager.shouldUseReactPlayer(),
                tvOptimized: deviceInfo.isTV
            }
        }, 'QRCode');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Preload overlay system during QR code display for instant transition
    useEffect(() => {
        if (!window.TrillboardsOverlay && !document.getElementById('trillboards-overlay-script')) {
            logger.info('Preloading overlay system while QR code is displayed', null, 'QRCode');
            const overlayVersion = process.env.REACT_APP_VERSION || 'dev';

            const script = document.createElement('script');
            script.id = 'trillboards-overlay-script';
            script.src = `${resolveRuntimeAssetUrl('trillboards-overlay.js')}?v=${encodeURIComponent(overlayVersion)}`;
            script.async = true;
            script.dataset.loaded = 'false';

            script.onload = () => {
                script.dataset.loaded = 'true';
                logger.info('Overlay system preloaded successfully - ready for instant transition', null, 'QRCode');
            };

            script.onerror = () => {
                script.dataset.loadFailed = 'true';
                script.remove();
                logger.warn('Failed to preload overlay system - will load on demand', null, 'QRCode');
            };

            document.head.appendChild(script);
        } else if (window.TrillboardsOverlay) {
            logger.debug('Overlay system already loaded - skipping preload', null, 'QRCode');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Browser geolocation removed: stationary DOOH screens get coords from server-side canonical sources
    // (partner-API supplied, operator portal-entered address forward-geocoded, IP-fallback). Partner Android
    // WebViews (e.g. Chrome 83 on legacy hardware) crashed on the permission prompt, killing the WebView session
    // before page load. The server's precedence chain at attach-screen + the screenEnrichmentQueue async backfill
    // is the canonical resolver now. See feedback_no_sync_in_onboarding_hotpath.md.

    const qrValue = useMemo(() => {
        if (!myFingerPrint) return '';

        // The website is a static export: /activate exists, but /activate/{fingerprint}
        // cannot resolve for arbitrary devices. Keep the fingerprint in the query.
        const baseUrl = 'https://trillboards.com/activate';
        const params = new URLSearchParams();
        params.set('fp', myFingerPrint);
        const query = params.toString();

        return query ? `${baseUrl}?${query}` : baseUrl;
    }, [myFingerPrint]);

    // Helper function to check screen status and auto-connect if available
    const checkScreenStatusAndConnect = async () => {
        try {
            logger.info("Checking screen status for automatic connection", null, "QRCode");
            const response = await axios.get(buildCheckScreenUrl(myFingerPrint), {
                headers: getDeviceHeaders(),
                timeout: CHECK_SCREEN_REQUEST_TIMEOUT_MS
            });
            handleDeviceTokenResponse(response, myFingerPrint);
            
            logger.debug("Screen status response", { response: response.data }, "QRCode");
            
            if (response?.data?.status === true && response?.data?.data?._id) {
                logger.info("Screen available - initiating automatic connection", null, "QRCode");
                
                // Stop any ongoing polling
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                    setIsPolling(false);
                }
                
                // Set up automatic connection
                const autoScreenId = response.data.data._id;
                persistScreenId(autoScreenId, myFingerPrint);
                setmyId(autoScreenId);
                setconnected(true);
                setloader(false);

                // Geo is resolved server-side via the precedence chain (partner-API / operator portal /
                // forward-geocode / IP-fallback). No browser geolocation call needed.

                logger.info("Automatic connection completed successfully", null, "QRCode");
                return true;
            } else {
                logger.debug("Screen not yet available for connection", null, "QRCode");
                return false;
            }
            
        } catch (error) {
            console.error("❌ Error checking screen status:", error);
            return false;
        }
    };

    // Function to prefetch content for instant display after screen connects
    const prefetchContent = async (screenId) => {
        if (!screenId) return null;

        try {
            logger.info("Prefetching content for instant display", { screenId }, "QRCode");
            const response = await axios.get(buildAppUrl(`/v2/earner/alot-advertisement-list/${screenId}`), {
                headers: { "Content-Type": 'application/json' },
                timeout: CONTENT_PREFETCH_TIMEOUT_MS
            });

            if (response?.data?.status === true && response?.data?.data) {
                const content = response.data.data;
                const hasStreams = content.all_default_streams?.length > 0;
                const hasAds = content.overlay_advertisements?.length > 0;
                const hasLBar = content.l_bar_content?.enabled;

                logger.info("Content prefetched successfully", { hasStreams, hasAds, hasLBar }, "QRCode");
                return { content, timestamp: Date.now() };
            }
            return null;
        } catch (error) {
            logger.warn("Content prefetch failed", { error: error?.message }, "QRCode");
            return null;
        }
    };

    // This effect drives initial screen bootstrap and deliberate retries.
    useEffect(() => {
        if (myFingerPrint) {
            setloader(true);
            const getdata = async () => {
                const reusableCachedScreenId = getReusableCachedScreenId({
                    currentFingerprint: myFingerPrint,
                    cachedScreenId: urlScreenId || readLocalStorageItem('screenId'),
                    cachedScreenFingerprint: urlScreenId ? myFingerPrint : readLocalStorageItem('trillboard_screen_identity_fingerprint'),
                    hasUrlFingerprint
                });
                const maxCheckScreenRetries = CHECK_SCREEN_MAX_RETRIES_WITHOUT_CACHE;

                if (reusableCachedScreenId) {
                    logger.info("Cached screen identity found; waiting for backend confirmation before showing content", {
                        cachedScreenId: reusableCachedScreenId
                    }, "QRCode");
                }

                for (let checkAttempt = 0; checkAttempt <= maxCheckScreenRetries; checkAttempt++) {
                  try {
                    logger.trace("Checking screen with fingerprint", { fingerprint: myFingerPrint, attempt: checkAttempt + 1 }, "QRCode");
                    logger.trace("API URL", { url: buildCheckScreenUrl(myFingerPrint) }, "QRCode");

                    const response = await axios.get(buildCheckScreenUrl(myFingerPrint), {
                        headers: getDeviceHeaders(),
                        timeout: CHECK_SCREEN_REQUEST_TIMEOUT_MS
                    });
                    handleDeviceTokenResponse(response, myFingerPrint);

                    logger.trace("Check screen response", { response: response.data }, "QRCode");
                    logger.trace("Response status", { status: response?.data?.status }, "QRCode");
                    logger.trace("Response data", { data: response?.data?.data }, "QRCode");

                    if (response?.data?.status === false) {
                        // Pairing code now comes straight from check-screen (~0.5s),
                        // fully decoupled from the slow socket + attach-screen chain
                        // below. Set it immediately so 'Generating…' is replaced
                        // almost instantly. The socket/attach flow still runs (for
                        // dimensions/content) and re-sets the same code later — this
                        // just no longer waits on it.
                        try {
                            const code = response?.data?.data?.pairing_code;
                            const expiresAt = response?.data?.data?.pairing_code_expires_at;
                            if (code) {
                                setPairingCode(String(code));
                                setPairingCodeExpiresAt(expiresAt || null);
                                logger.info("Pairing code received from check-screen", { code, expiresAt }, "QRCode");
                            }
                        } catch (pairErr) {
                            logger.warn("Failed to read pairing code from check-screen response", { error: pairErr?.message }, "QRCode");
                        }

                        if (reusableCachedScreenId) {
                            logger.warn("Backend reports no screen for cached identity - clearing stale cached screen before onboarding", {
                                cachedScreenId: reusableCachedScreenId
                            }, "QRCode");
                            clearCachedScreenState({
                                setMyId: setmyId,
                                setConnected: setconnected,
                                setLoader: setloader,
                                setCachedId: setCachedScreenId,
                                setPrefetched: setPrefetchedContent,
                                reason: 'backend_reported_screen_missing'
                            });
                        }
                        logger.info("Creating new socket connection", { fingerprint: myFingerPrint }, "QRCode");
                        const socketHost = window.__TRILLBOARDS_SOCKET_BASE || 'https://chat.trillboards.com';
                        const socket = io(socketHost, {
                            auth: deviceToken ? { deviceToken } : {},
                            query: { fingerprint: myFingerPrint },
                            // Dedicated screen sockets should stay on websocket only.
                            // Polling fallback makes reconnect placement depend on ALB
                            // cookie stickiness during rolling ECS deploys.
                            transports: ['websocket'],
                            reconnection: true,
                            reconnectionAttempts: Infinity,
                            reconnectionDelay: 1000,
                            reconnectionDelayMax: 30000,
                            randomizationFactor: 0.5,
                            timeout: 20000
                        });
                        setmysocket(socket);
                        let shutdownReconnectDelayMs = null;
                        let shutdownReconnectTimer = null;
                        const clearShutdownReconnectTimer = () => {
                            if (shutdownReconnectTimer) {
                                window.clearTimeout(shutdownReconnectTimer);
                                shutdownReconnectTimer = null;
                            }
                        };
                        const scheduleShutdownReconnect = (reason) => {
                            if (shutdownReconnectTimer) {
                                return;
                            }
                            const delayMs = shutdownReconnectDelayMs ?? (Math.floor(Math.random() * 15000) + 1000);
                            logger.info("Scheduling socket reconnect after server shutdown", { reason, delayMs }, "QRCode");
                            shutdownReconnectTimer = window.setTimeout(() => {
                                shutdownReconnectTimer = null;
                                try {
                                    if (socket.connected) {
                                        socket.disconnect();
                                    }
                                } catch (_) {}
                                logger.info("Reconnecting socket after shutdown drain", { reason, delayMs }, "QRCode");
                                socket.connect();
                            }, delayMs);
                        };

                        // ECS graceful shutdown: spread reconnections over 15s to avoid thundering herd
                        socket.on('server:shutdown', (payload) => {
                            const maxJitterMs = Number(payload?.reconnectJitterMaxMs ?? 15000);
                            shutdownReconnectDelayMs = Math.floor(Math.random() * Math.max(1000, maxJitterMs)) + 1000;
                            scheduleShutdownReconnect('server:shutdown');
                        });

                        socket.on('connect', () => {
                            clearShutdownReconnectTimer();
                            shutdownReconnectDelayMs = null;
                            logger.info("WebSocket connected", { socketId: socket.id, host: socketHost }, "QRCode");

                            const addScreen = async () => {
                                const widthPixels = window.screen.width;
                                const heightPixels = window.screen.height;
                                const dpi = window.devicePixelRatio * 96; // Approximate DPI

                                const widthInches = widthPixels / dpi;
                                const heightInches = heightPixels / dpi;

                                logger.info("Attaching screen with dimensions", {
                                    width: widthInches,
                                    height: heightInches,
                                    fingerprint: myFingerPrint,
                                    socketId: socket.id
                                });

                                // Fetch high-entropy Client Hints for authentic device identity
                                let clientHints = null;
                                if (navigator.userAgentData?.getHighEntropyValues) {
                                    try {
                                        const HINT_TIMEOUT_MS = 500;
                                        clientHints = await Promise.race([
                                            navigator.userAgentData.getHighEntropyValues([
                                                'architecture', 'bitness', 'model', 'platform', 'platformVersion'
                                            ]),
                                            new Promise(resolve => setTimeout(() => resolve(null), HINT_TIMEOUT_MS))
                                        ]);
                                    } catch (e) {
                                        // Client Hints unavailable — fall through to ua-parser
                                    }
                                }

                                // Prepare comprehensive device data using DeviceManager
                                const deviceData = {
                                    // Basic device info (OpenRTB compatible)
                                    ua: deviceInfo.userAgent,
                                    type: deviceInfo.isTV ? 'tv' : deviceInfo.isMobile ? 'mobile' : deviceInfo.isTablet ? 'tablet' : 'desktop',
                                    make: deviceInfo.make || 'Unknown',
                                    model: clientHints?.model || deviceInfo.model || 'Unknown',
                                    os: clientHints?.platform || deviceInfo.os || deviceInfo.platform || 'Unknown',
                                    osv: clientHints?.platformVersion || deviceInfo.osVersion || 'Unknown',
                                    agentVersion: process.env.REACT_APP_VERSION || '1.0.0',
                                    clientHints: clientHints || null,

                                    // Hardware capabilities
                                    hwv: navigator.hardwareConcurrency || 'Unknown',
                                    h: heightPixels,
                                    w: widthPixels,
                                    ppi: Math.round(dpi),
                                    pxratio: deviceInfo.pixelRatio,

                                    // Network info
                                    connectiontype: navigator.connection?.effectiveType || 'unknown',
                                    carrier: navigator.connection?.type || 'unknown',

                                    // Additional capabilities
                                    js: 1,
                                    flashver: '0',
                                    language: navigator.language,

                                    // Extended device info for Trillboards
                                    ext: {
                                        // Device detection results
                                        deviceType: {
                                            isTV: deviceInfo.isTV,
                                            isFireTV: deviceInfo.isFireTV,
                                            isSamsungTV: deviceInfo.isSamsungTV,
                                            isLGTV: deviceInfo.isLGTV,
                                            isRoku: deviceInfo.isRoku,
                                            isChromecast: deviceInfo.isChromecast,
                                            isAppleTV: deviceInfo.isAppleTV,
                                            isMobile: deviceInfo.isMobile,
                                            isTablet: deviceInfo.isTablet,
                                            isDesktop: deviceInfo.isDesktop
                                        },

                                        // Browser capabilities
                                        browser: {
                                            isChrome: deviceInfo.isChrome,
                                            isSafari: deviceInfo.isSafari,
                                            isFirefox: deviceInfo.isFirefox,
                                            isEdge: deviceInfo.isEdge
                                        },

                                        // Screen info
                                        screen: {
                                            width: deviceInfo.screenWidth,
                                            height: deviceInfo.screenHeight,
                                            aspectRatio: deviceInfo.aspectRatio,
                                            isLargeScreen: deviceInfo.isLargeScreen,
                                            isWidescreen: deviceInfo.isWidescreen,
                                            touchPoints: deviceInfo.touchPoints
                                        },

                                        // Viewport info
                                        viewport: {
                                            width: viewport.width,
                                            height: viewport.height,
                                            usableWidth: viewport.usableWidth,
                                            usableHeight: viewport.usableHeight,
                                            offsetX: viewport.offsetX,
                                            offsetY: viewport.offsetY
                                        },

                                        // Capabilities
                                        capabilities: {
                                            canPlayVideo: capabilities.canPlayVideo,
                                            canCreateIframe: capabilities.canCreateIframe,
                                            supportsAutoplay: capabilities.supportsAutoplay,
                                            isHighPerformance: capabilities.isHighPerformance,
                                            isLowPerformance: capabilities.isLowPerformance,
                                            supportsLocalStorage: capabilities.supportsLocalStorage,
                                            supportsViewportUnits: capabilities.supportsViewportUnits,
                                            supportsFlexbox: capabilities.supportsFlexbox
                                        },

                                        // ReactPlayer recommendations
                                        reactPlayer: {
                                            shouldUse: deviceManager.shouldUseReactPlayer(),
                                            config: deviceManager.getReactPlayerConfig()
                                        }
                                    }
                                };

                                // Generate session tracking data
                                const sessionId = getOrCreateSessionId();
                                const sessionFingerprint = generateSessionFingerprint(sessionId);

                                logger.debug("Adding session data", {
                                    sessionId,
                                    sessionFingerprint,
                                    fingerprint: myFingerPrint
                                });

                                const formdata = new FormData();
                                formdata.append('dimension_width', widthInches);
                                formdata.append('dimension_height', heightInches);
                                formdata.append('fingerPrint', myFingerPrint);
                                formdata.append('ppi', dpi);
                                formdata.append('socket_id', socket.id);
                                if (isNativeCtvBootstrap) {
                                    formdata.append('strict_install', '1');
                                    if (nativeFirstInstallTimeMs) {
                                        formdata.append('native_first_install_time_ms', nativeFirstInstallTimeMs);
                                    }
                                }
                                // location/lat/long deliberately omitted: server resolves via precedence chain
                                // (partner-API → operator portal → forward-geocode of address → IP-fallback).

                                // Add session tracking data
                                formdata.append('sessionId', sessionId);
                                formdata.append('sessionFingerprint', sessionFingerprint);

                                // Add OpenRTB device data
                                formdata.append('device_data', JSON.stringify(deviceData));

                                // Collect network signals for cross-device identity resolution
                                try {
                                    const networkSignals = await collectNetworkSignals();
                                    if (networkSignals?.localIpHash) formdata.append('local_ip_hash', networkSignals.localIpHash);
                                    if (networkSignals?.networkType) formdata.append('network_type', networkSignals.networkType);
                                    if (networkSignals?.downlinkMbps != null) formdata.append('downlink_mbps', String(networkSignals.downlinkMbps));
                                    if (networkSignals?.rttMs != null) formdata.append('rtt_ms', String(networkSignals.rttMs));
                                } catch (e) { /* non-fatal — signals are optional */ }

                                try {
                                    const attachResponse = await axios.post(
                                        buildAppUrl('/v2/earner/attach-screen'),
                                        formdata,
                                        {
                                            headers: getDeviceHeaders(null),
                                            timeout: ATTACH_SCREEN_TIMEOUT_MS
                                        }
                                    );
                                    handleDeviceTokenResponse(attachResponse, myFingerPrint);

                                    logger.debug("Attach screen response", { response: attachResponse.data }, "QRCode");

                                    // Pairing code (enterprise onboarding): display on-screen so operators can type it in the portal
                                    try {
                                        const code = attachResponse?.data?.data?.pairing_code;
                                        const expiresAt = attachResponse?.data?.data?.pairing_code_expires_at;
                                        if (code) {
                                            setPairingCode(String(code));
                                            setPairingCodeExpiresAt(expiresAt || null);
                                            logger.info("Pairing code received", { code, expiresAt }, "QRCode");
                                        }
                                    } catch (pairErr) {
                                        logger.warn("Failed to read pairing code from attach response", { error: pairErr?.message }, "QRCode");
                                    }

                                    if (attachResponse?.data?.status === true) {
                                        const attachData = attachResponse?.data?.data || {};
                                        const attachedScreenId = getAttachedScreenId(attachData);

                                        if (attachedScreenId) {
                                            persistScreenId(attachedScreenId, myFingerPrint);
                                            setmyId(attachedScreenId);
                                            logger.info("Screen attached successfully", { screenId: attachedScreenId }, "QRCode");
                                            setloader(false);
                                        } else {
                                            clearCachedScreenState({
                                                setMyId: setmyId,
                                                setConnected: setconnected,
                                                setLoader: setloader,
                                                setCachedId: setCachedScreenId,
                                                setPrefetched: setPrefetchedContent,
                                                reason: 'attach_returned_device_without_screen'
                                            });
                                            logger.info("Device attached; waiting for portal pairing", {
                                                deviceId: attachData?._id || null
                                            }, "QRCode");
                                        }
                                    }
                                } catch (err) {
                                    logger.error("Error attaching screen", { error: err?.message }, "QRCode");
                                    setloader(false);
                                }
                            };

                            addScreen();
                        });

                        socket.on('connect_error', (err) => {
                            logger.warn("WebSocket connect_error", { message: err?.message, host: socketHost }, "QRCode");
                        });

                        socket.on('reconnect_attempt', (attempt) => {
                            logger.info("WebSocket reconnect_attempt", { attempt, host: socketHost }, "QRCode");
                        });

                        socket.on('reconnect_failed', () => {
                            logger.warn("WebSocket reconnect_failed - will keep trying with exponential backoff", { host: socketHost }, "QRCode");
                        });

                        socket.on('reconnect', (attemptNumber) => {
                            clearShutdownReconnectTimer();
                            shutdownReconnectDelayMs = null;
                            logger.info(`WebSocket reconnected after ${attemptNumber} attempts`, { attemptNumber, host: socketHost }, "QRCode");
                        });

                        socket.on('disconnect', (reason) => {
                            logger.warn("WebSocket disconnected", { reason, host: socketHost }, "QRCode");
                            if (reason === 'io server disconnect' || (shutdownReconnectDelayMs !== null && reason === 'transport close')) {
                                scheduleShutdownReconnect(reason);
                            }
                        });

                        socket.on('connect_error', (error) => {
                            logger.error("Socket connection error", { error: error?.message }, "QRCode");
                        });

                        socket.on('disconnect', () => {
                            logger.info("WebSocket disconnected", null, "QRCode");
                        });

                        socket.on('receiveMessage', (data) => {
                            logger.trace("Received message from server", { data }, "QRCode");
                        });

                        socket.on('chatmessage', (data) => {
                            logger.trace("Received chat message from server", { data }, "QRCode");
                        });

                        // Enhanced socket listeners for seamless transitions
                        socket.on('screenConnected', () => {
                            logger.info("Screen connected event received - checking for automatic transition", null, "QRCode");
                            checkScreenStatusAndConnect();
                        });

                        socket.on('refreshAllScreens', () => {
                            logger.info("Refresh all screens event received - checking for automatic transition", null, "QRCode");
                            checkScreenStatusAndConnect();
                        });

                        socket.on('screenControl', (data) => {
                            logger.info("Screen control event received", { data }, "QRCode");
                            try {
                                const controlData = typeof data === 'string' ? JSON.parse(data) : data;

                                if (isDetachOrDeleteSignal(controlData)) {
                                    resetToPairingMode(controlData, socket);
                                    return;
                                }

                                if (controlData?.action === 'screen_connected' ||
                                    controlData?.action === 'refresh' ||
                                    controlData?.screenSaved === true) {
                                    logger.info("Screen control indicates screen is ready - initiating automatic connection", null, "QRCode");
                                    checkScreenStatusAndConnect();
                                }
                            } catch (error) {
                                console.warn("Failed to parse screenControl data:", error);
                                // Still try to check screen status as fallback
                                checkScreenStatusAndConnect();
                            }
                        });

                        socket.on('privateMessage', async (data) => {
                            logger.trace("Received private message", { data }, "QRCode");
                            try {
                                const messageData = JSON.parse(data);
                                if (isDetachOrDeleteSignal(messageData)) {
                                    resetToPairingMode(messageData, socket);
                                    return;
                                }
                                const shouldCheck = messageData?.fetchAdd === true ||
                                    messageData?.action === 'screen_connected' ||
                                    messageData?.screenSaved === true ||
                                    messageData?.action === 'refresh' ||
                                    messageData?.action === 'screen_updated' ||
                                    messageData?.action === 'attach_ack';
                                if (shouldCheck) {
                                    logger.info("Screen transition doorbell received - starting parallel prefetch", { action: messageData?.action }, "QRCode");

                                    // Check screen status and prefetch content in parallel for instant display
                                    try {
                                        const statusResponse = await axios.get(buildCheckScreenUrl(myFingerPrint), {
                                            headers: getDeviceHeaders(),
                                            timeout: CHECK_SCREEN_REQUEST_TIMEOUT_MS
                                        });
                                        handleDeviceTokenResponse(statusResponse, myFingerPrint);

                                        if (statusResponse?.data?.status === true && statusResponse?.data?.data?._id) {
                                            const screenId = statusResponse.data.data._id;
                                            logger.info("Screen confirmed connected - prefetching content", { screenId }, "QRCode");

                                            // Prefetch content for instant display
                                            const prefetched = await prefetchContent(screenId);
                                            if (prefetched) {
                                                setPrefetchedContent(prefetched);
                                                logger.info("Content prefetched - transitioning with instant display ready", null, "QRCode");
                                            }

                                            // Set connection state
                                            persistScreenId(screenId, myFingerPrint);
                                            setmyId(screenId);
                                            setconnected(true);
                                            setloader(false);
                                        } else {
                                            logger.debug("Screen not yet available during privateMessage check", null, "QRCode");
                                        }
                                    } catch (statusError) {
                                        logger.warn("Screen status check failed during privateMessage", { error: statusError?.message }, "QRCode");
                                        // Fallback to original behavior
                                        checkScreenStatusAndConnect();
                                    }
                                }

                            } catch (parseError) {
                                console.warn("Failed to parse privateMessage data:", parseError);

                                // Fallback: if we can't parse but this looks like a screen update, check status
                                if (typeof data === 'string' && (data.includes('screen') || data.includes('connect'))) {
                                    logger.debug("Unparseable message that might indicate screen update, checking status", null, "QRCode");
                                    checkScreenStatusAndConnect();
                                }
                            }
                        });

                        socket.on('screen_binding', (data) => {
                            logger.info("Screen binding event received", { data }, "QRCode");
                            if (data?.screenId === null || isDetachOrDeleteSignal(data)) {
                                resetToPairingMode(data, socket);
                                return;
                            }
                            if (data?.action === 'paired' && data?.screenId) {
                                checkScreenStatusAndConnect();
                            }
                        });
                    }
                    else if (response?.data?.status === true) {
                        logger.info("Screen already exists - prefetching content for instant display", { screenId: response?.data?.data?._id }, "QRCode");
                        logger.debug("Screen name", { screenName: response?.data?.data?.screenName }, "QRCode");
                        logger.debug("Screen data", { data: response?.data?.data }, "QRCode");

                        const screenId = response.data.data._id;

                        persistScreenId(screenId, myFingerPrint);
                        setmyId(screenId);
                        setconnected(true);
                        setloader(false);
                        logger.info("Connection state set to true; prefetch continuing in background", null, "QRCode");

                        prefetchContent(screenId).then((prefetched) => {
                            if (prefetched) {
                                setPrefetchedContent(prefetched);
                                logger.info("Content prefetched for existing screen", null, "QRCode");
                            }
                        }).catch((prefetchError) => {
                            logger.warn("Background prefetch failed after connected boot", { error: prefetchError?.message }, "QRCode");
                        });
                    }
                    break; // Success — exit retry loop
                  } catch (err) {
                    const isTransientError = err.response?.status >= 500 || err.response?.status === 401 || !err.response;

                    if (reusableCachedScreenId && isTransientError) {
                      logger.warn("check-screen failed but have cached screenId — using cached state immediately", { cachedScreenId: reusableCachedScreenId, attempts: checkAttempt + 1 }, 'QRCode');
                      enterCachedScreenState({
                        screenId: reusableCachedScreenId,
                        fingerprint: myFingerPrint,
                        setMyId: setmyId,
                        setConnected: setconnected,
                        setLoader: setloader,
                        setCachedId: setCachedScreenId,
                        reason: 'check_screen_transient_failure'
                      });

                      // Start background reconnect loop — keeps trying every 2 min
                      // until the API comes back, then re-syncs screen state properly.
                      // This handles outages lasting hours: the screen shows cached
                      // content immediately, and auto-heals when the API recovers.
                      const RECONNECT_INTERVAL = 120000; // 2 minutes
                      if (reconnectIntervalRef.current) clearInterval(reconnectIntervalRef.current);
                      let reconnectAttempt = 0;
                      reconnectIntervalRef.current = setInterval(async () => {
                        reconnectAttempt++;
                        try {
                          logger.info(`Background reconnect attempt ${reconnectAttempt}`, null, 'QRCode');
                          const reconnectResponse = await axios.get(
                            buildCheckScreenUrl(myFingerPrint),
                            {
                              headers: getDeviceHeaders(),
                              timeout: BACKGROUND_RECONNECT_TIMEOUT_MS
                            }
                          );
                          handleDeviceTokenResponse(reconnectResponse, myFingerPrint);

                          if (reconnectResponse?.data?.status === true && reconnectResponse?.data?.data?._id) {
                            const freshScreenId = reconnectResponse.data.data._id;
                            logger.info("Background reconnect succeeded — re-synced screen state", { freshScreenId, attempts: reconnectAttempt }, 'QRCode');
                            persistScreenId(freshScreenId, myFingerPrint);
                            setmyId(freshScreenId);
                            setconnected(true);
                            clearInterval(reconnectIntervalRef.current);
                            reconnectIntervalRef.current = null;
                          }
                        } catch (reconnectErr) {
                          logger.warn(`Background reconnect attempt ${reconnectAttempt} failed (${reconnectErr.response?.status || 'network'})`, null, 'QRCode');
                        }
                      }, RECONNECT_INTERVAL);

                      return; // Don't show QR code
                    }

                    if (checkAttempt < maxCheckScreenRetries && isTransientError) {
                      const delay = Math.min(
                        CHECK_SCREEN_RETRY_BASE_DELAY_MS * Math.pow(2, checkAttempt),
                        CHECK_SCREEN_RETRY_MAX_DELAY_MS
                      );
                      logger.warn(`check-screen attempt ${checkAttempt + 1} failed (${err.response?.status || 'network'}), retrying in ${delay}ms`, null, 'QRCode');
                      await new Promise(r => setTimeout(r, delay));
                      continue;
                    }

	                    if (reusableCachedScreenId) {
	                      logger.warn("check-screen rejected cached screen state - returning to onboarding", {
	                        cachedScreenId: reusableCachedScreenId,
	                        status: err.response?.status || null,
	                        error: err?.message
	                      }, "QRCode");
	                      clearCachedScreenState({
	                        setMyId: setmyId,
	                        setConnected: setconnected,
	                        setLoader: setloader,
	                        setCachedId: setCachedScreenId,
	                        setPrefetched: setPrefetchedContent,
	                        reason: 'check_screen_non_transient_failure'
	                      });
	                      return;
	                    }

	                    // No cached state — genuinely new device or non-transient error
	                    logger.error("check-screen failed, no cached state", { error: err?.message, attempts: checkAttempt + 1 }, "QRCode");
	                    setloader(false);
                  }
                }
            };
            
            getdata();
            logger.trace("Screen dimensions", { height: window.innerHeight, width: window.innerWidth }, "QRCode");
        }
    }, [appBase, buildAppUrl, myFingerPrint]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup polling and reconnect intervals on component unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                logger.debug("Cleaning up polling interval on component unmount", null, "QRCode");
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            if (reconnectIntervalRef.current) {
                logger.debug("Cleaning up reconnect interval on component unmount", null, "QRCode");
                clearInterval(reconnectIntervalRef.current);
                reconnectIntervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (
            connected ||
            !loader ||
            !hasCachedNativeIdentity({
                hasUrlFingerprint,
                cachedScreenId,
                urlDeviceToken,
                deviceToken
            })
        ) {
            return undefined;
        }

        const reusableCachedScreenId = getReusableCachedScreenId({
            currentFingerprint: myFingerPrint,
            cachedScreenId: urlScreenId || cachedScreenId,
            cachedScreenFingerprint: urlScreenId
                ? myFingerPrint
                : readLocalStorageItem('trillboard_screen_identity_fingerprint'),
            hasUrlFingerprint
        });

        const fallbackTimer = window.setTimeout(() => {
            logger.warn("Native CTV cached bootstrap timed out", {
                timeoutMs: CTV_BOOTSTRAP_VISIBLE_FALLBACK_MS,
                cachedScreenId: cachedScreenId || null,
                reusableCachedScreenId,
                hasUrlDeviceToken: Boolean(urlDeviceToken),
                hasDeviceToken: Boolean(deviceToken)
            }, "QRCode");

            if (reusableCachedScreenId) {
                enterCachedScreenState({
                    screenId: reusableCachedScreenId,
                    fingerprint: myFingerPrint,
                    setMyId: setmyId,
                    setConnected: setconnected,
                    setLoader: setloader,
                    setCachedId: setCachedScreenId,
                    reason: 'native_bootstrap_timeout'
                });
                return;
            }

            clearStoredDeviceToken();
            persistScreenId('');
            setCachedScreenId('');
            setmyId('');
            setconnected(false);
            setPrefetchedContent(null);
            setPairingCode('');
            setPairingCodeExpiresAt(null);
            setloader(false);
        }, CTV_BOOTSTRAP_VISIBLE_FALLBACK_MS);

        return () => {
            window.clearTimeout(fallbackTimer);
        };
    }, [cachedScreenId, connected, deviceToken, hasUrlFingerprint, loader, myFingerPrint, urlDeviceToken, urlScreenId]); // eslint-disable-line react-hooks/exhaustive-deps

    const showBootstrapLoader = shouldShowBootstrapLoader({
        hasFingerprint: Boolean(myFingerPrint),
        loader,
        connected,
        hasUrlFingerprint,
        cachedScreenId,
        urlDeviceToken,
        deviceToken
    });

    useEffect(() => {
        if (typeof window === 'undefined' || window.parent === window) {
            return;
        }

        let visibleState = null;
        if (!connected && !showBootstrapLoader && !storageBlocked && myFingerPrint) {
            visibleState = 'pairing';
        } else if (connected && myId) {
            visibleState = 'connected-shell';
        }

        if (!visibleState) {
            return;
        }

        window.parent.postMessage({
            type: 'trillboards:screen-visible',
            state: visibleState,
            hasPairingCode: Boolean(pairingCode),
            screenId: connected ? myId : null
        }, '*');
    }, [connected, myId, myFingerPrint, pairingCode, showBootstrapLoader, storageBlocked]);

    return (
        <>
            {/* Temporary Debug Button - Remove before production */}
            {process.env.NODE_ENV === 'development' && (
                <>
                    <div style={{
                        position: 'fixed', 
                        top: '10px', 
                        right: '10px', 
                        zIndex: 9999,
                        background: 'rgba(67, 97, 238, 0.9)',
                        color: 'white',
                        padding: '10px',
                        borderRadius: '8px',
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        logger.debug("Manual toggle - Current connected state", { connected }, "QRCode");
                        logger.debug("Current fingerprint", { fingerprint: myFingerPrint }, "QRCode");
                        setconnected(!connected);
                        setmyId("debug-screen-id");
                    }}
                    >
                        DEBUG: Toggle Connected ({connected ? 'Connected' : 'Disconnected'})
                    </div>
                    
                    {/* Device Detection Debug Panel */}
                    <div style={{
                        position: 'fixed',
                        bottom: '10px',
                        right: '10px',
                        width: '300px',
                        maxHeight: '400px',
                        overflow: 'auto',
                        background: 'rgba(0, 0, 0, 0.9)',
                        color: '#00ff00',
                        padding: '15px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        zIndex: 9998
                    }}>
                        <h4 style={{margin: '0 0 10px 0'}}>Device Manager Info</h4>
                        
                        <div style={{marginBottom: '10px'}}>
                            <strong>Device:</strong><br/>
                            TV: {deviceInfo.isTV ? 'Yes' : 'No'}<br/>
                            {deviceInfo.isTV && (
                                <>
                                    Fire TV: {deviceInfo.isFireTV ? 'Yes' : 'No'}<br/>
                                    Samsung: {deviceInfo.isSamsungTV ? 'Yes' : 'No'}<br/>
                                    LG: {deviceInfo.isLGTV ? 'Yes' : 'No'}<br/>
                                </>
                            )}
                            Mobile: {deviceInfo.isMobile ? 'Yes' : 'No'}<br/>
                            Tablet: {deviceInfo.isTablet ? 'Yes' : 'No'}
                        </div>
                        
                        <div style={{marginBottom: '10px'}}>
                            <strong>Screen:</strong><br/>
                            Size: {deviceInfo.screenWidth}x{deviceInfo.screenHeight}<br/>
                            Ratio: {deviceInfo.aspectRatio.toFixed(2)}<br/>
                            Pixel Ratio: {deviceInfo.pixelRatio}<br/>
                            Touch: {deviceInfo.touchPoints} points
                        </div>
                        
                        <div style={{marginBottom: '10px'}}>
                            <strong>Viewport:</strong><br/>
                            Size: {Math.round(viewport.width)}x{Math.round(viewport.height)}<br/>
                            Usable: {Math.round(viewport.usableWidth)}x{Math.round(viewport.usableHeight)}<br/>
                            Offset: {viewport.offsetX},{viewport.offsetY}
                        </div>
                        
                        <div style={{marginBottom: '10px'}}>
                            <strong>Browser:</strong><br/>
                            Chrome: {deviceInfo.isChrome ? 'Yes' : 'No'}<br/>
                            Safari: {deviceInfo.isSafari ? 'Yes' : 'No'}<br/>
                            Firefox: {deviceInfo.isFirefox ? 'Yes' : 'No'}
                        </div>
                        
                        <div style={{marginBottom: '10px'}}>
                            <strong>Capabilities:</strong><br/>
                            Video: {capabilities.canPlayVideo ? 'Yes' : 'No'}<br/>
                            Iframe: {capabilities.canCreateIframe ? 'Yes' : 'No'}<br/>
                            Autoplay: {capabilities.supportsAutoplay ? 'Yes' : 'No'}<br/>
                            High Perf: {capabilities.isHighPerformance ? 'Yes' : 'No'}
                        </div>
                        
                        <div style={{marginBottom: '10px'}}>
                            <strong>ReactPlayer:</strong><br/>
                            Should Use: {deviceManager.shouldUseReactPlayer() ? 'Yes' : 'No'}
                        </div>
                        
                        <div style={{marginTop: '10px', fontSize: '10px'}}>
                            Fingerprint: {myFingerPrint?.substring(0, 20)}...
                        </div>
                    </div>
                </>
            )}
            
            {(!connected && !debugMode) ?
                <>
                    {storageBlocked ? (
                        <div className="main_wrapper">
                            {/* Animated Background Waves */}
                            <div className="animated-background">
                                <div className="wave wave-1"></div>
                                <div className="wave wave-2"></div>
                                <div className="wave wave-3"></div>
                            </div>

                            <section className="trillboard_scanboard sucess_connect">
                                <div className="scanorcode_wrap" style={{
                                    maxWidth: '600px',
                                    margin: '0 auto',
                                    textAlign: 'center',
                                    padding: '40px 20px'
                                }}>
                                    <div style={{
                                        background: 'rgba(255, 82, 82, 0.1)',
                                        border: '2px solid rgba(255, 82, 82, 0.5)',
                                        borderRadius: '12px',
                                        padding: '30px',
                                        marginBottom: '20px'
                                    }}>
                                        <h2 style={{
                                            color: '#ff5252',
                                            fontSize: '24px',
                                            marginBottom: '20px'
                                        }}>
                                            🔒 Storage Blocked
                                        </h2>
                                        <p style={{
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            fontSize: '16px',
                                            lineHeight: '1.6',
                                            marginBottom: '20px'
                                        }}>
                                            Your browser is blocking cookies and local storage, which are required for Trillboards to work.
                                        </p>
                                        <div style={{
                                            background: 'rgba(0, 0, 0, 0.3)',
                                            borderRadius: '8px',
                                            padding: '20px',
                                            textAlign: 'left'
                                        }}>
                                            <h3 style={{
                                                color: 'var(--secondary-color)',
                                                fontSize: '18px',
                                                marginBottom: '15px'
                                            }}>
                                                To fix this in your TV browser:
                                            </h3>
                                            <div style={{
                                                color: 'rgba(255, 255, 255, 0.85)',
                                                fontSize: '14px',
                                                lineHeight: '1.7',
                                                marginBottom: '20px'
                                            }}>
                                                <strong style={{color: 'var(--secondary-color)'}}>Option 1: Enable cookies in your current browser</strong><br/>
                                                1. Look for the Menu button (three dots ⋮ or three lines ☰)<br/>
                                                2. Find Settings<br/>
                                                3. Look for Privacy, Security, or Site Settings<br/>
                                                4. Turn ON "Allow cookies" or "Enable local storage"<br/>
                                                5. Reload this page
                                            </div>
                                            <div style={{
                                                color: 'rgba(255, 255, 255, 0.85)',
                                                fontSize: '14px',
                                                lineHeight: '1.7'
                                            }}>
                                                <strong style={{color: 'var(--secondary-color)'}}>Option 2: Download a better browser</strong><br/>
                                                1. Go to your TV's app store (Google Play Store, App Store, etc.)<br/>
                                                2. Search for "Silk Browser" or "TV Bro" or "Puffin Browser"<br/>
                                                3. Download and open it<br/>
                                                4. Try loading this page again
                                            </div>
                                        </div>
                                        <p style={{
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontSize: '14px',
                                            marginTop: '20px'
                                        }}>
                                            After enabling storage, refresh this page.
                                        </p>
                                    </div>
                                </div>
                            </section>
                        </div>
                    ) : showBootstrapLoader ? <div id="loader-overlay">
                        <div className="loader"></div>
                    </div>
                        :
                        <div className="main_wrapper">
                            {/* Animated Background Waves */}
                            <div className="animated-background">
                                <div className="wave wave-1"></div>
                                <div className="wave wave-2"></div>
                                <div className="wave wave-3"></div>
                            </div>
                            <section className="trillboard_scanboard sucess_connect">
                                <div className="scanorcode_wrap">
                                    {/* Device info display */}
                                    {process.env.NODE_ENV === 'development' && (
                                        <div style={{
                                            fontSize: '14px',
                                            color: 'rgba(1, 190, 165, 0.8)',
                                            marginBottom: '10px',
                                            textAlign: 'center'
                                        }}>
                                            Detected: {deviceInfo.isTV ? `TV (${deviceInfo.isFireTV ? 'Fire TV' : deviceInfo.isSamsungTV ? 'Samsung' : deviceInfo.isLGTV ? 'LG' : 'Generic'})` : deviceInfo.isMobile ? 'Mobile' : deviceInfo.isTablet ? 'Tablet' : 'Desktop'} | 
                                            {deviceInfo.screenWidth}x{deviceInfo.screenHeight} | 
                                            ReactPlayer: {deviceManager.shouldUseReactPlayer() ? 'Yes' : 'No'}
                                        </div>
                                    )}
                                    
                                    {/* Show polling status when waiting for user to save screen */}
                                    {isPolling && (
                                        <div 
                                            className="polling-status"
                                            style={{
                                                background: 'rgba(1, 190, 165, 0.1)',
                                                border: '1px solid rgba(1, 190, 165, 0.3)',
                                                borderRadius: '8px',
                                                padding: '8px 16px',
                                                margin: '10px 0',
                                                color: 'var(--secondary-color)',
                                                fontSize: 'var(--small-font-size)',
                                                textAlign: 'center'
                                            }}
                                        >
                                            QR code opened. Finish adding this screen in the Earner Portal...
                                        </div>
                                    )}
                                    
                                    {/* Connection Steps - Split to left and right sides */}
                                    <div className="connection-steps-sides">
                                        <div className="steps-left">
                                            <div className="step-item">
                                                <span className="step-emoji">🌐</span>
                                                <span className="step-text">Scan this QR code with your phone</span>
                                            </div>
                                            <div className="step-item">
                                                <span className="step-emoji">🔐</span>
                                                <span className="step-text">Sign in to the Earner Portal</span>
                                            </div>
                                        </div>
                                        
	                                        <div className="qr-center">
	                                            {/* Pairing code displayed ABOVE QR code for visibility */}
	                                            <div className="pairing-code-box" role="status" aria-live="polite">
	                                                <div className="pairing-code-label">Pairing code</div>
	                                                <div className="pairing-code-value">
	                                                    {pairingCode || 'Generating…'}
	                                                </div>
		                                                <div className="pairing-code-hint">
		                                                    No scanner? Enter this code at trillboards.com/earner.
		                                                </div>
	                                                {pairingCodeExpiresAt ? (
	                                                    <div className="pairing-code-expiry">
	                                                        Expires: {new Date(pairingCodeExpiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
	                                                    </div>
	                                                ) : null}
	                                            </div>
	                                            <QRCode
                                                size={(() => {
                                                    const viewportHeight = window.innerHeight;
                                                    const viewportWidth = window.innerWidth;
                                                    const minDim = Math.min(viewportHeight, viewportWidth);
                                                    // Larger QR codes for TV screens (height >= 720)
                                                    const isLargeScreen = viewportHeight >= 720;
                                                    // TV: 35% of min dimension, capped at 400px
                                                    // Mobile/small: 30% of min dimension, capped at 300px
                                                    const maxSize = isLargeScreen
                                                        ? minDim * 0.35
                                                        : minDim * 0.3;
                                                    const maxCap = isLargeScreen ? 400 : 300;
                                                    return Math.max(180, Math.min(maxCap, maxSize));
                                                })()}
                                                style={{
                                                    height: "auto",
                                                    maxWidth: "min(400px, 40vmin)",
                                                    width: "auto",
                                                    display: "block"
                                                }}
                                                value={qrValue}
                                                logoImage={trillBoardLogo}
                                                logoPadding={1}
                                                logoHeight={(() => {
                                                    const baseSize = Math.min(window.innerHeight, window.innerWidth) * 0.3;
                                                    const logoSize = Math.max(30, Math.min(50, baseSize * 0.15));
                                                    return logoSize;
                                                })()}
                                                logoWidth={(() => {
                                                    const baseSize = Math.min(window.innerHeight, window.innerWidth) * 0.3;
                                                    const logoSize = Math.max(30, Math.min(50, baseSize * 0.15));
                                                    return logoSize;
                                                })()}
	                                            />
		                                            <h2>Scan to add this screen</h2>
	                                        </div>
                                        
                                        <div className="steps-right">
                                            <div className="step-item">
                                                <span className="step-emoji">📷</span>
                                                <span className="step-text">Confirm the screen details</span>
                                            </div>
                                            <div className="step-item">
                                                <span className="step-emoji">✅</span>
                                                <span className="step-text">Finish onboarding in the portal</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>}
                </>
                : <TVTestPlayer myFingerPrint={myFingerPrint} myId={myId} socket={mysocket} prefetchedContent={prefetchedContent} />}
        </>
    );
}

export default QRcode;
